import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button, Input, Label, Modal, Select } from '@/lib'

import { useOrderCenterCommentsCreate } from '@/services'
import { onErrorMessage } from '@/helpers'

import { SaleOrderCommentTypesEnum } from '@/types'
import {
  FinanceValidationFormEnum,
  ModalFinanceValidationProps,
} from './modal-finance-validation.type'
import styles from './modal-finance-validation.module.css'

export const ModalFinanceValidation = ({
  isOpen,
  form,
  saleOrderUuid = '',
  onClose,
}: ModalFinanceValidationProps) => {
  const { t } = useTranslation('componentOrderCenterForm')
  const [values, setValues] = useState({
    reason: '',
    comment: '',
  })

  const { mutateAsync, isPending } = useOrderCenterCommentsCreate(saleOrderUuid)

  const handleClickSubmit = async () => {
    const response = await mutateAsync({
      type:
        form === FinanceValidationFormEnum['payment-proof']
          ? SaleOrderCommentTypesEnum.orderpayment_proof
          : SaleOrderCommentTypesEnum.total_paid_value,
      subject: values.reason,
      comment: values.comment || t('financeValidationModalCommentNoComments'),
    })

    if (response.success) {
      onClose(true)

      return
    }

    onErrorMessage(
      t('financeValidationModalSubmitError'),
      t('financeValidationModalSubmitErrorDescription'),
    )
  }

  const handleClose = () => onClose()

  const forms = {
    'payment-proof': {
      title: t('financeValidationModalPaymentProofTitle'),
      description: t('financeValidationModalPaymentProofDescription'),
      reasonQuestion: t('financeValidationModalPaymentProofReasonTitle'),
      options: [
        {
          label: t('financeValidationModalPaymentProofReasonOption1'),
          value: t('financeValidationModalPaymentProofReasonOption1'),
        },
        {
          label: t('financeValidationModalPaymentProofReasonOption2'),
          value: t('financeValidationModalPaymentProofReasonOption2'),
        },
        {
          label: t('financeValidationModalPaymentProofReasonOption3'),
          value: t('financeValidationModalPaymentProofReasonOption3'),
        },
        {
          label: t('financeValidationModalPaymentProofReasonOption4'),
          value: t('financeValidationModalPaymentProofReasonOption4'),
        },
      ],
    },
    'total-ammount': {
      title: t('financeValidationModalTypeTotalAmount'),
      description: t('financeValidationModalTypeTotalAmountDescription'),
      reasonQuestion: t('financeValidationModalPaymentProofReasonTitle'),
      options: [
        {
          label: t('financeValidationModalTotalAmountOptionX1'),
          value: t('financeValidationModalTotalAmountOptionX1'),
        },
        {
          label: t('financeValidationModalTotalAmountOptionX2'),
          value: t('financeValidationModalTotalAmountOptionX2'),
        },
        {
          label: t('financeValidationModalTotalAmountOptionX3'),
          value: t('financeValidationModalTotalAmountOptionX3'),
        },
        {
          label: t('financeValidationModalTotalAmountOptionX4'),
          value: t('financeValidationModalTotalAmountOptionX4'),
        },
      ],
    },
  }

  return (
    <Modal.Root isOpen={isOpen} onHandleOpen={handleClose} size="small">
      <Modal.Header
        title={forms[form]?.title || ''}
        description={forms[form]?.description || ''}
        className={styles.header}
      />
      <Modal.Body className={styles.body}>
        <div className={styles.component}>
          <div>
            <Label required>{t('financeValidationModalReasonLabel')}</Label>
            <Select
              placeholder={t('financeValidationModalReasonPlaceholder')}
              options={forms[form]?.options}
              value={values.reason}
              onChange={(reason) => setValues((prev) => ({ ...prev, reason }))}
            />
          </div>

          <div>
            <Label>{t('financeValidationModalCommentLabel')}</Label>
            <Input
              isTextArea={true}
              value={values.comment}
              onChange={(comment) =>
                setValues((prev) => ({ ...prev, comment }))
              }
            />
          </div>

          <div className={styles.footer}>
            <Button variant="secondary" onClick={handleClose}>
              {t('financeValidationModalCancelButton')}
            </Button>

            <Button
              variant="primary"
              onClick={handleClickSubmit}
              isActive={!isPending}
              disabled={isPending || !values.reason}
            >
              {t('financeValidationModalSaveButton')}
            </Button>
          </div>
        </div>
      </Modal.Body>
    </Modal.Root>
  )
}
